import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { fetchAudienceIds, fetchStates, searchAudience } from './whatsappBroadcastApi';
import { AudienceFilters, AudienceCustomer } from './types';

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (filters: AudienceFilters, count: number) => void;
  initialFilters?: AudienceFilters;
}

const stripCustomerIds = (filters: AudienceFilters): AudienceFilters => {
  const { customer_ids: _ignored, ...rest } = filters;
  return rest;
};

const AudiencePickerModal: React.FC<Props> = ({ open, onClose, onApply, initialFilters = {} }) => {
  const [filters, setFilters] = useState<AudienceFilters>(initialFilters);
  const [states, setStates] = useState<string[]>([]);
  const [rows, setRows] = useState<AudienceCustomer[]>([]);
  const [matchingTotal, setMatchingTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const perPage = 25;

  const baseFilters = useMemo(() => stripCustomerIds(filters), [filters]);
  const filtersKey = useMemo(() => JSON.stringify(baseFilters), [baseFilters]);
  const prevFiltersKeyRef = useRef<string | null>(null);
  const selectedCount = selectedIds.size;

  useEffect(() => {
    if (!open) {
      prevFiltersKeyRef.current = null;
      return;
    }
    setFilters(initialFilters);
    setPage(0);
    fetchStates().then(setStates).catch(console.error);
  }, [open, initialFilters]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const isFirstLoad = prevFiltersKeyRef.current === null;
    prevFiltersKeyRef.current = filtersKey;

    const loadSelection = async () => {
      setLoading(true);
      try {
        const idResult = await fetchAudienceIds(baseFilters);
        if (cancelled) return;
        setMatchingTotal(idResult.total);

        if (isFirstLoad && initialFilters.customer_ids?.length) {
          const allowed = new Set(idResult.customer_ids);
          setSelectedIds(new Set(initialFilters.customer_ids.filter((id) => allowed.has(id))));
        } else {
          setSelectedIds(new Set(idResult.customer_ids));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSelection();
    return () => {
      cancelled = true;
    };
  }, [open, filtersKey, baseFilters, initialFilters.customer_ids]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const loadPage = async () => {
      setLoading(true);
      try {
        const search = await searchAudience(baseFilters, page, perPage);
        if (cancelled) return;
        setRows(search.rows);
        setTotal(search.total);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPage();
    return () => {
      cancelled = true;
    };
  }, [open, baseFilters, page]);

  const updateFilters = (updater: (current: AudienceFilters) => AudienceFilters) => {
    setPage(0);
    setFilters((current) => updater(stripCustomerIds(current)));
  };

  const toggleCustomer = (customerId: number, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(customerId);
      else next.delete(customerId);
      return next;
    });
  };

  const pageIds = rows.map((row) => row.customer_id);
  const pageSelectedCount = pageIds.filter((id) => selectedIds.has(id)).length;
  const allOnPageSelected = pageIds.length > 0 && pageSelectedCount === pageIds.length;
  const someOnPageSelected = pageSelectedCount > 0 && !allOnPageSelected;

  const togglePageSelection = (checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const id of pageIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const selectAllMatching = async () => {
    setLoading(true);
    try {
      const idResult = await fetchAudienceIds(baseFilters);
      setMatchingTotal(idResult.total);
      setSelectedIds(new Set(idResult.customer_ids));
    } finally {
      setLoading(false);
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleApply = () => {
    onApply(
      {
        ...baseFilters,
        customer_ids: Array.from(selectedIds),
      },
      selectedCount
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select audience</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            label="Phone"
            size="small"
            value={filters.phone || ''}
            onChange={(e) => updateFilters((f) => ({ ...f, phone: e.target.value }))}
          />
          <TextField
            label="Company / name search"
            size="small"
            value={filters.customer_search || ''}
            onChange={(e) => updateFilters((f) => ({ ...f, customer_search: e.target.value }))}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>State</InputLabel>
            <Select
              label="State"
              value={filters.state || ''}
              onChange={(e) => updateFilters((f) => ({ ...f, state: e.target.value || undefined }))}
            >
              <MenuItem value="">All states</MenuItem>
              {states.map((state) => (
                <MenuItem key={state} value={state}>{state}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(filters.whatsapp_alert_subscribed)}
                onChange={(e) => updateFilters((f) => ({
                  ...f,
                  whatsapp_alert_subscribed: e.target.checked ? true : undefined,
                }))}
              />
            }
            label="Subscribed only"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(filters.whatsapp_alert_marked_premium)}
                onChange={(e) => updateFilters((f) => ({
                  ...f,
                  whatsapp_alert_marked_premium: e.target.checked ? true : undefined,
                }))}
              />
            }
            label="Premium only"
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2">
            Selected: <strong>{selectedCount}</strong> of <strong>{matchingTotal}</strong> matching
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={selectAllMatching} disabled={loading}>Select all matching</Button>
            <Button size="small" onClick={clearSelection} disabled={loading || selectedCount === 0}>Clear selection</Button>
          </Box>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allOnPageSelected}
                  indeterminate={someOnPageSelected}
                  onChange={(e) => togglePageSelection(e.target.checked)}
                  disabled={rows.length === 0}
                />
              </TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Subscribed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={`${row.customer_id}-${row.phone_10}`}
                hover
                selected={selectedIds.has(row.customer_id)}
                onClick={() => toggleCustomer(row.customer_id, !selectedIds.has(row.customer_id))}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.has(row.customer_id)}
                    onChange={(e) => toggleCustomer(row.customer_id, e.target.checked)}
                  />
                </TableCell>
                <TableCell>{row.customer_name || row.customer_id}</TableCell>
                <TableCell>{row.phone_mobile || row.phone_10}</TableCell>
                <TableCell>{row.address_state || '—'}</TableCell>
                <TableCell>{row.whatsapp_alert_subscribed ? 'Yes' : 'No'}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>No customers match these filters</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
          <Typography variant="body2">
            Page {page + 1} · {total} total
          </Typography>
          <Button disabled={(page + 1) * perPage >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleApply} disabled={selectedCount === 0 || loading}>
          Use {selectedCount} recipients
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AudiencePickerModal;
